import { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DollarSign, CheckCircle, Clock, Download, FileText, Loader2 } from 'lucide-react';
import { formatDate, formatTime, getDurationLabel } from '../../lib/utils';
import { toast } from 'sonner';

export default function InstructorPayouts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    setLoading(true);
    api.get('/api/finance/my-payouts', { params: { month } })
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  }, [month]);

  const downloadInvoice = async () => {
    setDownloading(true);
    try {
      // V13.4 — try the new compliance-grade invoice first; fall back to the legacy
      // /generate-invoice if the compliance one returns 4xx (e.g. period out of range).
      let res;
      try {
        res = await api.get(`/api/finance/my-invoice?month=${month}`, { responseType: 'blob' });
      } catch (e) {
        res = await api.get(`/api/finance/generate-invoice?month=${month}`, { responseType: 'blob' });
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SoulSanskar-Invoice-${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice. No completed sessions for this month.');
    }
    setDownloading(false);
  };

  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en', { month: 'long', year: 'numeric' }) };
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">My Payouts</h1>
          <p className="text-muted-foreground mt-1">View your earnings and download invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadInvoice} disabled={downloading} data-testid="download-invoice">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Invoice
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
              <DollarSign className="h-5 w-5 text-primary mb-3" />
              <p className="text-2xl font-semibold">INR {(data?.total_payout || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Payout</p>
            </CardContent></Card>
            <Card className="rounded-2xl border-amber-200"><CardContent className="pt-5 pb-4">
              <Clock className="h-5 w-5 text-amber-600 mb-3" />
              <p className="text-2xl font-semibold text-amber-900">INR {(data?.pending_payout || 0).toLocaleString()}</p>
              <p className="text-xs text-amber-700">Pending</p>
            </CardContent></Card>
            <Card className="rounded-2xl border-emerald-200"><CardContent className="pt-5 pb-4">
              <CheckCircle className="h-5 w-5 text-emerald-600 mb-3" />
              <p className="text-2xl font-semibold text-emerald-900">INR {(data?.approved_payout || 0).toLocaleString()}</p>
              <p className="text-xs text-emerald-700">Approved</p>
            </CardContent></Card>
          </div>

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <FileText className="h-5 w-5" />Session Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.sessions?.length === 0 ? (
                <div className="text-center py-8"><p className="text-muted-foreground">No completed sessions for this month</p></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Batch</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Payout</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.sessions || []).map(s => (
                      <TableRow key={s.id}>
                        <TableCell>{formatDate(s.scheduled_date)}</TableCell>
                        <TableCell>{formatTime(s.scheduled_time)}</TableCell>
                        <TableCell className="font-medium">{s.batch_name}</TableCell>
                        <TableCell>{getDurationLabel(s.duration_minutes)}</TableCell>
                        <TableCell className="font-semibold">INR {(s.payout_amount || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={s.payout_approved ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}>
                            {s.payout_approved ? 'Approved' : 'Pending'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Invoice Info */}
          <Card className="rounded-2xl bg-muted/40">
            <CardContent className="pt-5 pb-4">
              <p className="text-sm font-medium">Invoice Guidelines</p>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                <li>Raise your invoice by the <strong>24th of every month</strong></li>
                <li>Download the invoice PDF and email it to the Soul Sanskar team</li>
                <li>The admin will verify and approve each session payout</li>
                <li>Payout is based on <strong>scheduled class duration</strong>, not actual duration</li>
              </ul>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
